import { CartItem, Product } from '@pedix-workspace/utils';
import { StockInfo, StockItemType } from './stock-info.model';
import {
  optionToStockItem,
  presentationToStockItem,
  productToStockItem,
  unkownStockItem,
} from './utils';
import { StockDelta } from './stock-delta.model';

export type StockInfoMap = Record<string, StockInfo>;
export type StockProduct = Pick<
  Product,
  'id' | 'name' | 'stockQty' | 'hidden' | 'presentations' | 'options' | 'linkedOptionIds'
> & { active?: boolean };

export class StockMap {
  private stockInfoMap: Record<string, StockInfo>;

  constructor(
    private products: StockProduct[],
    private cartItems: CartItem[],
    private originalCartItems?: CartItem[],
  ) {
    this.stockInfoMap = this.processStockInfoMap(
      this.products,
      this.cartItems,
      this.originalCartItems,
    );
  }

  getStockInfoByItem(itemId: string): StockInfo {
    return this.stockInfoMap[itemId] || new StockInfo(unkownStockItem(itemId));
  }

  getItemsWithUnknownStockInfo(): StockInfo[] {
    return Object.values(this.stockInfoMap).filter(stockInfo => stockInfo.status === 'unknown');
  }

  getStockInfoForCartItems(): StockInfo[] {
    return Object.values(this.stockInfoMap).filter(stockInfo => stockInfo.totalStockInCart > 0);
  }

  getStockInfoDeltas(): StockDelta[] {
    const stockDeltas: StockDelta[] = [];

    Object.values(this.stockInfoMap).forEach(stockInfo => {
      if (stockInfo.hasUnlimitedStock) {
        return;
      }
      if (stockInfo.totalStockInCart > 0) {
        const stockDifference = stockInfo.totalStockInCart - stockInfo.reservedStockForOrder;

        stockDeltas.push({
          item: stockInfo.item,
          delta: stockDifference === 0 ? 0 : stockDifference * -1,
        });
      } else if (stockInfo.reservedStockForOrder > 0) {
        stockDeltas.push({
          item: stockInfo.item,
          delta: stockInfo.reservedStockForOrder,
        });
      }
    });

    return stockDeltas;
  }

  getStockInfoArrayByCartItem(cartItemId: string): StockInfo[] {
    return this.getStockInfoForCartItems().filter(
      stockInfo => stockInfo.getConsumedStock(cartItemId) > 0,
    );
  }

  getItemsInCartWithStockErrors(): StockInfo[] {
    return this.getStockInfoForCartItems().filter(stockInfo => stockInfo.hasMissingStock);
  }

  private processStockInfoMap(
    products: StockProduct[],
    cartItems: CartItem[],
    originalCartItems?: CartItem[],
  ) {
    const stockInfoMap: StockInfoMap = {};

    // 1. Initializes base stock for products
    products.forEach(product => {
      stockInfoMap[product.id] = new StockInfo(productToStockItem(product));

      if (product.presentations) {
        product.presentations.items?.forEach(presentation => {
          stockInfoMap[presentation.id] = new StockInfo(
            presentationToStockItem(presentation, product),
          );
        });
      }

      if (product.options) {
        product.options.forEach(option => {
          option.items?.forEach(item => {
            stockInfoMap[item.id] = new StockInfo(optionToStockItem(item, product));
          });
        });
      }
    });

    // 2. If there are originalCartItems, increases base stock from "reserved" stock
    if (originalCartItems) {
      originalCartItems.forEach(cartItem => {
        if (cartItem.selectedPresentation) {
          cartItem.selectedPresentation.items.forEach(presentation => {
            if (stockInfoMap[presentation.id]) {
              const consumedStock = presentation.quantity || cartItem.qty;

              stockInfoMap[presentation.id].setReservedStock(consumedStock);
            }
          });
        } else {
          if (stockInfoMap[cartItem.product.id]) {
            stockInfoMap[cartItem.product.id].setReservedStock(cartItem.qty);
          }
        }
        if (cartItem.selectedOptions) {
          cartItem.selectedOptions.forEach(selectedOption => {
            selectedOption.items.forEach(item => {
              if (stockInfoMap[item.id]) {
                stockInfoMap[item.id].setReservedStock((item.quantity || 1) * cartItem.qty);
              }
            });
          });
        }
      });
    }

    // 3. Discount stock quantity from cartItems
    cartItems.forEach(cartItem => {
      if (cartItem.selectedPresentation) {
        cartItem.selectedPresentation.items.forEach(presentation => {
          if (stockInfoMap[presentation.id]) {
            const consumedStock = presentation.quantity || cartItem.qty;

            stockInfoMap[presentation.id].setConsumedStock(consumedStock, cartItem.id);
          } else {
            stockInfoMap[presentation.id] = new StockInfo(
              presentationToStockItem(presentation, cartItem.product),
              'unknown',
            );
          }
        });
      } else {
        if (stockInfoMap[cartItem.product.id]) {
          stockInfoMap[cartItem.product.id].setConsumedStock(cartItem.qty, cartItem.id);
        } else {
          stockInfoMap[cartItem.product.id] = new StockInfo(
            productToStockItem(cartItem.product),
            'unknown',
          );
        }
      }
      if (cartItem.selectedOptions) {
        cartItem.selectedOptions.forEach(selectedOption => {
          selectedOption.items.forEach(item => {
            if (stockInfoMap[item.id]) {
              stockInfoMap[item.id].setConsumedStock(
                (item.quantity || 1) * cartItem.qty,
                cartItem.id,
              );
            } else {
              stockInfoMap[item.id] = new StockInfo(
                optionToStockItem(item, cartItem.product),
                'unknown',
              );
            }
          });
        });
      }
    });

    return stockInfoMap;
  }
}
